import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CurrencyEur, Receipt, Bank, FileArrowDown } from "@phosphor-icons/react";

const eur = (c) => `€${((c || 0) / 100).toFixed(2)}`;

export default function BookkeepingPanel({ tenant }) {
  const [tab, setTab] = useState("credit_notes");
  return (
    <div className="space-y-5" data-testid="bookkeeping-panel">
      <h2 className="font-heading text-2xl font-bold tracking-tight">Gutschriften & SEPA</h2>
      <div className="flex flex-wrap gap-2">
        {[["credit_notes","Gutschriften"],["sepa","Sammellastschrift"],["sepa-config","Gläubiger-Daten"]].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 text-xs rounded-full font-medium border transition ${tab === k ? "bg-black text-white border-black" : "bg-white border-black/10 text-zinc-700 hover:bg-zinc-50"}`}
            data-testid={`bk-tab-${k}`}>{l}</button>
        ))}
      </div>
      {tab === "credit_notes" && <CreditNotes />}
      {tab === "sepa" && <SepaBatches />}
      {tab === "sepa-config" && <SepaConfig tenant={tenant} />}
    </div>
  );
}

function CreditNotes() {
  const [notes, setNotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({ customer_id: "", amount: "", mode: "offset", reason: "", refund_iban: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const reload = async () => setNotes((await api.get("/credit-notes")).data.credit_notes || []);
  useEffect(() => { reload(); api.get("/customers").then((r) => setCustomers(r.data || [])); }, []);

  const submit = async () => {
    setBusy(true); setMsg(null);
    try {
      await api.post("/credit-notes", {
        customer_id: form.customer_id,
        amount_cents: Math.round(parseFloat(form.amount || 0) * 100),
        mode: form.mode, reason: form.reason,
        refund_iban: form.refund_iban || undefined,
      });
      setForm({ customer_id: "", amount: "", mode: "offset", reason: "", refund_iban: "" });
      setMsg({ ok: true, msg: "Erstellt" }); await reload();
      setTimeout(() => setMsg(null), 2500);
    } catch (e) { setMsg({ ok: false, msg: e.response?.data?.detail || "Fehler" }); }
    finally { setBusy(false); }
  };
  const applyNote = async (id) => {
    if (!window.confirm("Mit nächster offener Rechnung verrechnen?")) return;
    try { const r = await api.post(`/credit-notes/${id}/apply`, {}); setMsg({ ok: true, msg: `Verrechnet: ${r.data.invoice_id}` }); await reload(); }
    catch (e) { setMsg({ ok: false, msg: e.response?.data?.detail || "Fehler" }); }
  };
  const refundNote = async (id) => {
    if (!window.confirm("Als Rücküberweisung markieren?")) return;
    try { await api.post(`/credit-notes/${id}/refund`, {}); setMsg({ ok: true, msg: "Auszahlung markiert" }); await reload(); }
    catch (e) { setMsg({ ok: false, msg: e.response?.data?.detail || "Fehler" }); }
  };

  return (
    <div className="space-y-5">
      <div className="bg-white border border-black/10 p-5 rounded-lg" data-testid="cn-form">
        <h3 className="font-heading text-lg font-semibold flex items-center gap-2 mb-3"><CurrencyEur size={18} weight="bold" /> Neue Gutschrift</h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-xs">Kunde</Label>
            <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
              <SelectTrigger className="h-10 rounded-none" data-testid="cn-customer-select"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
              <SelectContent>
                {customers.map((c) => (
                  <SelectItem key={c.customer_id} value={c.customer_id}>
                    {c.customer_number || ""} · {c.company_name || `${c.first_name} ${c.last_name}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Betrag (€)</Label><Input type="number" step="0.01" min="0" className="h-10 rounded-none" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="cn-amount-input" /></div>
          <div>
            <Label className="text-xs">Modus</Label>
            <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
              <SelectTrigger className="h-10 rounded-none" data-testid="cn-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="offset">Verrechnung</SelectItem>
                <SelectItem value="refund">Rücküberweisung</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><Label className="text-xs">Grund</Label><Input className="h-10 rounded-none" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="z.B. Kulanz Frost-Ausfall" data-testid="cn-reason-input" /></div>
          {form.mode === "refund" && (
            <div className="sm:col-span-2"><Label className="text-xs">IBAN (Rücküberweisung)</Label><Input className="h-10 rounded-none font-mono" value={form.refund_iban} onChange={(e) => setForm({ ...form, refund_iban: e.target.value })} data-testid="cn-iban-input" /></div>
          )}
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Button onClick={submit} disabled={busy || !form.customer_id || !form.amount} className="rounded-none bg-black text-white hover:bg-zinc-800 h-10" data-testid="cn-submit-btn">
            {busy ? "Erstelle…" : "Gutschrift erstellen"}
          </Button>
          {msg && <span className={`text-xs font-medium ${msg.ok ? "text-emerald-700" : "text-red-600"}`}>{msg.ok ? "✓" : "✗"} {msg.msg}</span>}
        </div>
      </div>

      <div className="bg-white border border-black/10 p-5 rounded-lg">
        <h3 className="font-heading text-lg font-semibold flex items-center gap-2 mb-3"><Receipt size={18} weight="bold" /> Gutschriften ({notes.length})</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="cn-table">
            <thead className="text-xs uppercase text-zinc-500 border-b border-black/5">
              <tr><th className="text-left py-2">Nr.</th><th className="text-left py-2">Kunde</th><th className="text-right py-2">Betrag</th><th className="text-left py-2 pl-4">Modus</th><th className="text-left py-2 pl-4">Status</th><th className="text-right py-2">Aktion</th></tr>
            </thead>
            <tbody>
              {notes.map((n) => (
                <tr key={n.credit_note_id} className="border-b border-black/5" data-testid={`cn-row-${n.credit_note_id}`}>
                  <td className="py-2 font-mono text-xs">{n.issued_number}</td>
                  <td className="py-2 text-xs">{n.customer_id.slice(-8)}</td>
                  <td className="py-2 text-right font-bold">{eur(n.amount_cents)}</td>
                  <td className="py-2 pl-4 text-xs">{n.mode === "offset" ? "Verrechnung" : "Rücküberweisung"}</td>
                  <td className="py-2 pl-4"><span className={`text-xs px-2 py-0.5 rounded-full ${n.status === "issued" ? "bg-blue-100 text-blue-700" : n.status === "applied" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{n.status}</span></td>
                  <td className="py-2 text-right">
                    {n.status === "issued" && (
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => applyNote(n.credit_note_id)} className="text-xs px-2 py-1 rounded hover:bg-emerald-100 text-emerald-700" data-testid={`cn-apply-${n.credit_note_id}`}>Verrechnen</button>
                        {n.refund_iban && <button onClick={() => refundNote(n.credit_note_id)} className="text-xs px-2 py-1 rounded hover:bg-amber-100 text-amber-700" data-testid={`cn-refund-${n.credit_note_id}`}>Auszahlen</button>}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {notes.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-zinc-400 italic">Noch keine Gutschriften</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SepaBatches() {
  const [batches, setBatches] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState([]);
  const [execDate, setExecDate] = useState(new Date(Date.now() + 5 * 86400000).toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const reload = async () => {
    try {
      const b = await api.get("/sepa/batches");
      setBatches(b.data.batches || []);
    } catch (e) { console.warn("[Bookkeeping] batches load failed", e); }
    try {
      const inv = await api.get("/invoices");
      setInvoices((inv.data.invoices || inv.data || []).filter((i) => i.status !== "paid"));
    } catch (e) { console.warn("[Bookkeeping] invoices load failed", e); }
  };
  useEffect(() => { reload(); }, []);

  const createBatch = async () => {
    if (!selected.length) return;
    setBusy(true); setMsg(null);
    try {
      await api.post("/sepa/batches", { invoice_ids: selected, execution_date: execDate });
      setSelected([]); setMsg({ ok: true, msg: "Batch erzeugt" }); await reload();
      setTimeout(() => setMsg(null), 2500);
    } catch (e) { setMsg({ ok: false, msg: e.response?.data?.detail || "Fehler" }); }
    finally { setBusy(false); }
  };
  const download = async (id) => {
    try {
      const r = await api.get(`/sepa/batches/${id}/xml`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `sepa-${id}.xml`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 500);
    } catch (e) { console.error("[Bookkeeping] download failed", e); alert("Download fehlgeschlagen. Bitte erneut versuchen."); }
  };

  return (
    <div className="space-y-5">
      <div className="bg-white border border-black/10 p-5 rounded-lg" data-testid="sepa-create">
        <h3 className="font-heading text-lg font-semibold flex items-center gap-2 mb-3"><Bank size={18} weight="bold" /> Neuer SEPA-Batch</h3>
        <p className="text-xs text-zinc-500 mb-4">Wählen Sie Rechnungen für die Sammellastschrift. Voraussetzung: Kunde hat gültiges SEPA-Mandat und die Gläubiger-Daten sind gepflegt.</p>
        <div className="max-h-72 overflow-y-auto border border-black/5 rounded" data-testid="sepa-invoice-list">
          {invoices.length === 0 && <div className="p-4 text-xs text-zinc-400 italic">Keine offenen Rechnungen.</div>}
          {invoices.map((inv) => (
            <label key={inv.invoice_id} className="flex items-center gap-3 p-3 border-b border-black/5 hover:bg-zinc-50 cursor-pointer">
              <input type="checkbox" checked={selected.includes(inv.invoice_id)}
                onChange={(e) => setSelected(e.target.checked ? [...selected, inv.invoice_id] : selected.filter((x) => x !== inv.invoice_id))}
                className="w-5 h-5" data-testid={`sepa-inv-${inv.invoice_id}`} />
              <div className="flex-1 text-sm">
                <div className="font-mono font-bold">{inv.number || inv.invoice_id}</div>
                <div className="text-xs text-zinc-500">{inv.customer_name || inv.customer_id}</div>
              </div>
              <div className="font-bold">{eur((inv.total_cents || inv.amount_cents || 0) - (inv.credit_applied_cents || 0))}</div>
            </label>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-[200px_1fr_auto] gap-3 items-end">
          <div><Label className="text-xs">Fälligkeit</Label><Input type="date" className="h-10 rounded-none" value={execDate} onChange={(e) => setExecDate(e.target.value)} data-testid="sepa-exec-input" /></div>
          <div className="text-xs text-zinc-500 pb-2"><strong>{selected.length}</strong> Rechnung(en) ausgewählt · Summe {eur(invoices.filter((i) => selected.includes(i.invoice_id)).reduce((s, i) => s + (i.total_cents || i.amount_cents || 0) - (i.credit_applied_cents || 0), 0))}</div>
          <Button onClick={createBatch} disabled={busy || !selected.length} className="rounded-none bg-black text-white hover:bg-zinc-800 h-10 disabled:opacity-50" data-testid="sepa-create-btn">{busy ? "Erzeuge…" : "Batch erzeugen"}</Button>
        </div>
        {msg && <div className={`mt-2 text-xs font-medium ${msg.ok ? "text-emerald-700" : "text-red-600"}`}>{msg.ok ? "✓" : "✗"} {msg.msg}</div>}
      </div>

      <div className="bg-white border border-black/10 p-5 rounded-lg">
        <h3 className="font-heading text-lg font-semibold flex items-center gap-2 mb-3"><FileArrowDown size={18} weight="bold" /> SEPA-Batches ({batches.length})</h3>
        <table className="w-full text-sm" data-testid="sepa-batches-table">
          <thead className="text-xs uppercase text-zinc-500 border-b border-black/5"><tr><th className="text-left py-2">Message-ID</th><th className="text-left py-2">Bank-Profil</th><th className="text-left py-2">Fällig</th><th className="text-right py-2">Anzahl</th><th className="text-right py-2">Summe</th><th className="text-right py-2">XML</th></tr></thead>
          <tbody>
            {batches.map((b) => (
              <tr key={b.batch_id} className="border-b border-black/5">
                <td className="py-2 font-mono text-xs">{b.message_id}</td>
                <td className="py-2 text-xs">{b.bank_profile}</td>
                <td className="py-2 text-xs">{b.execution_date}</td>
                <td className="py-2 text-right">{b.invoice_count}</td>
                <td className="py-2 text-right font-bold">{eur(b.total_amount_cents)}</td>
                <td className="py-2 text-right"><button onClick={() => download(b.batch_id)} className="text-xs px-3 py-1 rounded hover:bg-blue-100 text-blue-700 font-medium" data-testid={`sepa-dl-${b.batch_id}`}>⬇ XML</button></td>
              </tr>
            ))}
            {batches.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-zinc-400 italic">Noch keine SEPA-Batches</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SepaConfig({ tenant }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => { api.get(`/tenants/${tenant.tenant_id}/sepa-config`).then((r) => setCfg(r.data)); }, [tenant?.tenant_id]);
  const save = async () => {
    setSaving(true);
    try { const r = await api.patch(`/tenants/${tenant.tenant_id}/sepa-config`, cfg); setCfg(r.data); setMsg({ ok: true, msg: "Gespeichert" }); setTimeout(() => setMsg(null), 2500); }
    catch (e) { setMsg({ ok: false, msg: e.response?.data?.detail || "Fehler" }); }
    finally { setSaving(false); }
  };
  if (!cfg) return <div className="text-sm text-zinc-400 p-6">Lade…</div>;
  return (
    <section className="bg-white border border-black/10 p-5 rounded-lg space-y-4" data-testid="sepa-config-section">
      <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><Bank size={18} weight="bold" /> Gläubiger-Daten</h3>
      <p className="text-xs text-zinc-500">Die Gläubiger-ID (Creditor-ID) erhalten Sie kostenlos bei der Deutschen Bundesbank. Das Bank-Profil steuert BIC-Pflicht + Zeichensatz.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label className="text-xs">Kontoinhaber</Label><Input className="h-10 rounded-none" value={cfg.creditor_name} onChange={(e) => setCfg({ ...cfg, creditor_name: e.target.value })} data-testid="sepa-cn-input" /></div>
        <div><Label className="text-xs">IBAN</Label><Input className="h-10 rounded-none font-mono" value={cfg.creditor_iban} onChange={(e) => setCfg({ ...cfg, creditor_iban: e.target.value })} data-testid="sepa-iban-input" /></div>
        <div><Label className="text-xs">BIC (optional bei DE)</Label><Input className="h-10 rounded-none font-mono" value={cfg.creditor_bic} onChange={(e) => setCfg({ ...cfg, creditor_bic: e.target.value })} data-testid="sepa-bic-input" /></div>
        <div><Label className="text-xs">Gläubiger-ID</Label><Input className="h-10 rounded-none font-mono" value={cfg.creditor_id} onChange={(e) => setCfg({ ...cfg, creditor_id: e.target.value })} placeholder="DE98ZZZ09999999999" data-testid="sepa-crid-input" /></div>
        <div className="sm:col-span-2">
          <Label className="text-xs">Bank-Profil</Label>
          <Select value={cfg.bank_profile} onValueChange={(v) => setCfg({ ...cfg, bank_profile: v })}>
            <SelectTrigger className="h-10 rounded-none" data-testid="sepa-bank-select"><SelectValue /></SelectTrigger>
            <SelectContent>{cfg.available_profiles.map((p) => <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={saving} className="rounded-none bg-black text-white hover:bg-zinc-800" data-testid="sepa-config-save">{saving ? "Speichere…" : "Speichern"}</Button>
        {msg && <span className={`text-xs font-medium ${msg.ok ? "text-emerald-700" : "text-red-600"}`}>{msg.ok ? "✓" : "✗"} {msg.msg}</span>}
      </div>
    </section>
  );
}
